import React, { useEffect, useState } from "react";
import { Engagespot } from "@engagespot/react-component";
import { useSelector } from "react-redux";

import { ReactComponent as NotificationIcon } from "../../assets/Notification.svg";

const apiKey = (process.env.REACT_APP_ENGAGESPOT_API_KEY || "").trim();
const SERVICE_WORKER_URL = "/service-worker.js";

const isEngagespotPushError = reason => {
  const msg = String(reason?.message || reason || "");
  return (
    /push service/i.test(msg) ||
    /ES100[4-6]/i.test(msg) ||
    /service worker must be registered/i.test(msg)
  );
};

/**
 * Engagespot notification bell.
 * Initializes only when REACT_APP_ENGAGESPOT_API_KEY is set and a user is signed in.
 * Web-push subscribe failures are soft-handled so the in-app feed still works.
 */
const NotificationBell = ({ className = "" }) => {
  const accessor = useSelector(state => state.accessor.accessor);
  const [swRegistration, setSwRegistration] = useState(null);
  const enabled = Boolean(apiKey && accessor?.uid);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
      return undefined;
    }

    let cancelled = false;

    const onUnhandledRejection = event => {
      if (!isEngagespotPushError(event.reason)) return;
      event.preventDefault();
      console.warn(
        "[Engagespot] web push unavailable; in-app notifications still work.",
        event.reason?.message || event.reason,
      );
    };
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    navigator.serviceWorker
      .register(SERVICE_WORKER_URL, { updateViaCache: "none" })
      .then(() => navigator.serviceWorker.ready)
      .then(registration => {
        if (cancelled) return;

        // Soft-fail pushManager.subscribe so Engagespot init does not crash the app.
        const pushManager = registration.pushManager;
        if (pushManager && !pushManager.__hackleSoftSubscribe) {
          const originalSubscribe = pushManager.subscribe.bind(pushManager);
          pushManager.subscribe = async options => {
            try {
              return await originalSubscribe(options);
            } catch (err) {
              console.warn(
                "[Engagespot] web push subscribe skipped:",
                err?.message || err,
              );
              return pushManager.getSubscription();
            }
          };
          pushManager.__hackleSoftSubscribe = true;
        }

        setSwRegistration(registration);
      })
      .catch(err => {
        console.warn("[Engagespot] service worker registration failed", err);
      });

    return () => {
      cancelled = true;
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, [enabled]);

  if (!enabled || !swRegistration) return null;

  return (
    <div
      className={`inline-flex items-center justify-center text-grey-700 hover:text-grey-900 ${className}`}
      aria-label='Notifications'>
      <Engagespot
        apiKey={apiKey}
        userId={accessor.uid}
        serviceWorkerRegistration={swRegistration}
        renderNotificationIcon={() => (
          <NotificationIcon className='h-6 w-6 fill-current' />
        )}
      />
    </div>
  );
};

export default NotificationBell;
